// ─── DEPLOYMENT QUICK REFERENCE ──────────────────────────────────────────────
//
//  1. BUILD + DEPLOY (Firebase Hosting + Functions)
//     npm run build
//     firebase deploy
//
//  2. DEPLOY HOSTING ONLY (faster, skips Functions)
//     npm run build
//     firebase deploy --only hosting
//
//  3. PUSH TO GIT (after deploying or making changes)
//     git add .
//     git commit -m "your message here"
//     git push
//
//  4. ALL IN ONE (build → deploy → push)
//     npm run build && firebase deploy && git add . && git commit -m "deploy" && git push
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect, Suspense, lazy, Component } from 'react';
import { Helmet } from 'react-helmet';
import AuthForm from './components/AuthForm';
import Welcome from './components/Welcome';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { completeGoogleRedirectSignIn, onAuthChange, logOut } from './firebase/authService';
import posthog from './posthog';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { mergeFirestoreStatsToLocal, flushUserStats, scheduleSaveUserStats } from './utils/firestoreSync';
import { applyStoryAudio } from './utils/storyAudio';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { SiTiktok } from 'react-icons/si';
import {
  trackPageView,
  trackStoryOpened,
  trackLogout as trackLogoutEvent,
  setUserProperties as setAnalyticsUserProps,
  trackContinueReading,
  trackNextStory,
  trackSocialClick,
  trackPurchase,
} from './utils/analytics';
import { getNextInChain } from './config/storyChain';
import { PAYWALL_ENABLED } from './config/featureFlags';
import { recordFreeStoryAccess } from './utils/freeStoryTracker';
import { isPublishedStory, isPublishedStoryFile } from './config/storyAvailability';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';

// Heavy route-level components are lazy-loaded so they don't bloat the initial bundle
const Home        = lazy(() => import('./components/Home'));
const Story       = lazy(() => import('./components/Story'));
const About       = lazy(() => import('./components/About'));
const Blog        = lazy(() => import('./components/Blog'));
const Account     = lazy(() => import('./components/Account'));
const Grammar     = lazy(() => import('./components/Grammar'));
const Videos      = lazy(() => import('./components/Videos'));
const VideoPlayer = lazy(() => import('./components/VideoPlayer'));

class ChunkErrorBoundary extends Component {
  state = { errored: false };
  static getDerivedStateFromError() { return { errored: true }; }
  render() {
    if (this.state.errored) {
      // Reload the page once to pick up the new JS chunks after a deploy
      if (!sessionStorage.getItem('chunkReloaded')) {
        sessionStorage.setItem('chunkReloaded', '1');
        window.location.reload();
        return null;
      }
      return <Box sx={{ minHeight: '100vh', background: '#FACF9F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Button onClick={() => window.location.reload()}>Reload page</Button></Box>;
    }
    return this.props.children;
  }
}

// ─── Access helpers (mirrors Home.js logic) ───────────────────────────────────
const hasFullAccess = (user) => Boolean(user?.premium);

// Returns true when a manifest entry is free (no subscription needed).
// Rules:
//   1. Title matches one of the FREE_STORY_TITLES ("Try These Stories" list)
//   2. It is episode 1 of any series
const FREE_STORY_TITLES = [
  'mushroom hotpot',
  'pool party',
];
const seriesKeyFromFilePath = (file) => {
  if (!file) return null;
  const parts = file.split('/');
  if (parts.length >= 3) {
    const folder = parts[1];
    return (!folder || folder.toUpperCase() === 'UNCATEGORISED') ? null : folder;
  }
  return null;
};
const isFreeStory = (entry, manifestList = []) => {
  if (!entry) return false;
  const normalizedTitle = String(entry.title || '').trim().toLowerCase();
  // Try These Stories
  if (FREE_STORY_TITLES.some(t => normalizedTitle.includes(t))) return true;
  // Episode 1 of any series
  const seriesKey = seriesKeyFromFilePath(entry.file);
  if (seriesKey && Array.isArray(manifestList) && manifestList.length > 0) {
    const seriesEps = manifestList
      .filter(m => seriesKeyFromFilePath(m.file) === seriesKey && !m.disabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (seriesEps.length > 0 && seriesEps[0].file === entry.file) return true;
  }
  return false;
};

// ─── StoryLoader ──────────────────────────────────────────────────────────────
// Resolves a story from the URL path. Fast-paths when selectedStory is already
// the correct file (normal in-app navigation). Falls back to fetching from the
// public Stories folder so direct links (shared URL, Google crawl) also work.

// Convert a manifest file path to its public URL slug (strips UNCATEGORISED):
// HSK3/UNCATEGORISED/Making_dumplings_with_family.json → HSK3/Making_dumplings_with_family
const fileToSlug = (file) => file.replace('.json', '').replace('/UNCATEGORISED/', '/');

function StoryLoader({ manifestStories, selectedStory, setSelectedStory, onNavigateAway, children }) {
  const { '*': splat } = useParams();
  const [currentFile, setCurrentFile] = React.useState(null);

  // Resolve the actual .json file path from the URL slug.
  // For clean URLs with UNCATEGORISED stripped, look up the manifest.
  // Falls back to direct (splat + .json) when manifest not yet loaded.
  const filePath = React.useMemo(() => {
    if (!splat) return null;
    const direct = `${splat}.json`;
    if (!isPublishedStoryFile(direct)) return null;
    if (manifestStories.length > 0) {
      if (manifestStories.some(s => s.file === direct)) return direct;
      const match = manifestStories.find(s => fileToSlug(s.file) === splat);
      if (match) return match.file;
      return null;
    }
    return direct;
  }, [splat, manifestStories]);

  React.useEffect(() => {
    if (!filePath) { onNavigateAway(); return; }
    const base = process.env.PUBLIC_URL || '';
    const entry = manifestStories.find(s => s.file === filePath) || {};

    // Fast-path: story already in state from in-app navigation.
    // Always patch audio/icon from the manifest — the manifest path is
    // authoritative and may differ from (or replace) what the story JSON had.
    // This also covers the race where the manifest loads *after* the story JSON
    // (the common case on direct URL / blog-post link navigation).
    if (selectedStory?.file === filePath) {
      let updated = null;
      if (entry.audioDisabled) {
        if (selectedStory.audio || !selectedStory.audioDisabled)
          updated = { ...(updated || selectedStory), audio: null, audioDisabled: true };
      } else if (entry.audio) {
        const rawAudio = entry.audio;
        const resolved = /^https?:\/\//.test(rawAudio) ? rawAudio : `${base}${rawAudio}`;
        if (resolved !== selectedStory.audio)
          updated = { ...(updated || selectedStory), audio: resolved };
      }
      if (entry.icon) {
        const resolved = /^https?:\/\//.test(entry.icon) ? entry.icon : `${base}${entry.icon}`;
        if (resolved !== selectedStory.icon)
          updated = { ...(updated || selectedStory), icon: resolved };
      }
      if (!selectedStory.series && entry.series) {
        updated = { ...(updated || selectedStory), series: entry.series };
      }
      if (updated) setSelectedStory(updated);
      setCurrentFile(filePath);
      return;
    }

    fetch(`${base}/Stories/${filePath}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(storyJson => {
        applyStoryAudio(entry, storyJson);
        if (storyJson.image && !/^https?:\/\//.test(storyJson.image)) storyJson.image = `${base}${storyJson.image}`;
        if (entry.icon) storyJson.icon = /^https?:\/\//.test(entry.icon) ? entry.icon : `${base}${entry.icon}`;
        storyJson.file = filePath;
        // Always use filePath as the canonical id so read-state keys are
        // consistent regardless of whether the story was opened via
        // in-app navigation (openGradedReader sets data.id = filePath) or
        // via a direct URL fetch (the JSON might have its own short id).
        storyJson.id = filePath;
        if (!storyJson.level && entry.level) storyJson.level = entry.level;
        if (!storyJson.series && entry.series) storyJson.series = entry.series;
        setSelectedStory(storyJson);
        setCurrentFile(filePath);
      })
      .catch(() => {
        // Don't navigate away if manifest hasn't loaded yet — filePath may still
        // resolve correctly once the manifest arrives and the effect re-runs.
        if (!manifestStories.length) return;
        onNavigateAway();
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, manifestStories.length]);

  if (!currentFile || currentFile !== filePath || !selectedStory || selectedStory.file !== filePath) {
    return <Box sx={{ minHeight: '100vh', background: '#FACF9F' }} />;
  }
  return children(selectedStory, fileToSlug(filePath));
}

// ─── GrammarLessonRoute ────────────────────────────────────────────────────────
// Reads :lessonSlug from the URL and passes it to Grammar so direct links from
// blog posts land on the correct lesson.
function GrammarLessonRoute() {
  const { lessonSlug } = useParams();
  const navigate = useNavigate();
  // Format slug into a readable title for static meta fallback (e.g. "ba-sentence" → "Ba Sentence")
  const lessonTitle = lessonSlug
    ? lessonSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Grammar';
  return (
    <>
      <Helmet>
        <title>{lessonTitle} - Chinese Grammar | Dumplingo</title>
        <meta name="description" content={`Learn ${lessonTitle} in Mandarin Chinese. Clear examples in Chinese, pinyin, and English. Free HSK grammar guide on Dumplingo.`} />
        <link rel="canonical" href={`https://dumplingo.net/grammar/${lessonSlug}`} />
        <meta property="og:url" content={`https://dumplingo.net/grammar/${lessonSlug}`} />
        <meta property="og:title" content={`${lessonTitle} - Chinese Grammar | Dumplingo`} />
        <meta property="og:description" content={`Learn ${lessonTitle} in Mandarin Chinese. Clear examples in Chinese, pinyin, and English. Free HSK grammar guide on Dumplingo.`} />
      </Helmet>
      <Grammar onGoHome={() => navigate('/')} initialLessonSlug={lessonSlug} />
    </>
  );
}

// ─── BlogPostRoute ─────────────────────────────────────────────────────────────
// Reads :postId from the URL and passes it to Blog as initialPostId so direct
// links and Googlebot can land on the correct post with the correct canonical.
function BlogPostRoute({ user, handleLogout, navigate }) {
  const { postId } = useParams();
  return (
    <Blog
      user={user}
      onAbout={() => navigate('/about')}
      onContact={() => {}}
      onLogin={() => navigate('/login')}
      onLogout={handleLogout}
      onGoHome={() => navigate('/')}
      onAccount={() => navigate('/account')}
      onNavigateToStories={() => navigate('/stories')}
      initialPostId={postId}
    />
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // CONSOLIDATED STATE
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showPinyin, setShowPinyin] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [lastReadStory, setLastReadStory] = useState(null);
  const [pendingSeries, setPendingSeries] = useState(null);
  const [manifestStories, setManifestStories] = useState([]);
  const [courseFileOrder, setCourseFileOrder] = React.useState([]); // ordered file_paths across themed courses
  const identifiedUserIdRef = useRef(null);

  // Track virtual page views on every route change
  // Pass story context when on the story page so GA4 session reports are useful
  useEffect(() => {
    const path = location.pathname;
    const pageName = path === '/' ? 'welcome' : path.replace(/^\//, '');
    if (pageName.startsWith('story/') && selectedStory) {
      trackPageView('story', {
        story_id: selectedStory.file || '',
        story_title: selectedStory.title || '',
        story_level: selectedStory.level || '',
      });
    } else {
      trackPageView(pageName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectedStory?.file]);

  // Sync user identity to GA4 whenever auth state or premium status changes
  useEffect(() => {
    setAnalyticsUserProps(user);
  }, [user?.uid, user?.premium]);

  // Fire purchase event and show celebration when premium activates after a checkout redirect
  useEffect(() => {
    if (!user?.premium) return;
    const pending = localStorage.getItem('pendingPurchaseTrack');
    if (pending) {
      trackPurchase(pending);
      localStorage.removeItem('pendingPurchaseTrack');
      setShowUpgradeSuccess(true);
    }
  }, [user?.premium]);

  // Poll for premium activation when returning from Stripe — webhook may not have fired yet
  useEffect(() => {
    if (!localStorage.getItem('pendingPurchaseTrack')) return;
    if (!user?.uid) return;
    if (user?.premium) return; // already active
    let attempts = 0;
    const MAX = 12; // poll up to ~60s
    const interval = setInterval(async () => {
      attempts++;
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists() && snap.data().premium) {
          setUser(prev => prev ? { ...prev, premium: true } : prev);
          clearInterval(interval);
        }
      } catch {}
      if (attempts >= MAX) clearInterval(interval);
    }, 5000);
    return () => clearInterval(interval);
  }, [user?.uid, user?.premium]);

  // Save learning stats to Firestore when the tab is hidden (more reliable than beforeunload)
  useEffect(() => {
    if (!user?.uid) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushUserStats(user.uid);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.uid]);

  // Firebase auth listener - resolve auth immediately, fetch premium in background
  React.useEffect(() => {
    completeGoogleRedirectSignIn();

    const unsubscribe = onAuthChange((firebaseUser) => {
      if (firebaseUser) {
        if (identifiedUserIdRef.current !== firebaseUser.uid) {
          if (identifiedUserIdRef.current) {
            posthog?.reset();
          }
          posthog?.identify(firebaseUser.uid, {
            email: firebaseUser.email || undefined,
            name: firebaseUser.displayName || undefined,
          });
          identifiedUserIdRef.current = firebaseUser.uid;
        }

        const baseUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          premium: false
        };

        // Set user immediately to avoid long auth loading
        setUser(baseUser);
        setAuthLoading(false);

        // Fetch user doc: restores premium status AND learning stats from Firestore
        (async () => {
          try {
            const db = getFirestore();
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            const userData = userDoc.exists() ? userDoc.data() : {};

            // Restore learning stats into localStorage (handles incognito / new device)
            mergeFirestoreStatsToLocal(firebaseUser.uid, userData);

            // Push any existing localStorage data to Firestore.
            // This is the bootstrap step for existing users who signed in before
            // Firestore sync was implemented — their data only lives in localStorage.
            scheduleSaveUserStats(firebaseUser.uid);

            setUser((prev) => (
              prev && prev.uid === firebaseUser.uid
                ? { ...prev, premium: userData.premium || false }
                : prev
            ));
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        })();
      } else {
        if (identifiedUserIdRef.current) {
          posthog?.reset();
          identifiedUserIdRef.current = null;
        }
        setUser(null);
        setAuthLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load / clear last-read story when auth state changes or when returning to welcome page
  React.useEffect(() => {
    if (user?.uid) {
      try {
        const raw = localStorage.getItem(`lastReadStory:${user.uid}`);
        setLastReadStory(raw ? JSON.parse(raw) : null);
      } catch {
        setLastReadStory(null);
      }
    } else {
      setLastReadStory(null);
    }
  }, [user?.uid, location.pathname]);

  // Keep lastReadStory in sync when Story.js advances it on mark-as-read
  React.useEffect(() => {
    const handler = (e) => { if (e.detail) setLastReadStory(e.detail); };
    window.addEventListener('last-read-story-updated', handler);
    return () => window.removeEventListener('last-read-story-updated', handler);
  }, []);

  // Fetch the stories manifest once so we can compute next-story suggestions
  React.useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ''}/Stories/manifest.json`)
      .then(r => r.json())
      .then(data => setManifestStories(Array.isArray(data) ? data.filter(isPublishedStory) : []))
      .catch(() => {});
  }, []);

  // Compute the next story using the master STORY_CHAIN order.
  // The chain is the single source of truth — to change what comes next,
  // edit src/config/storyChain.js.
  // If a course context is active (user came from a themed course), use course ordering instead.
  const nextStory = React.useMemo(() => {
    if (courseFileOrder.length > 0 && selectedStory?.file) {
      const idx = courseFileOrder.indexOf(selectedStory.file);
      if (idx >= 0) {
        for (let nextIndex = idx + 1; nextIndex < courseFileOrder.length; nextIndex += 1) {
          const entry = manifestStories.find(m => m.file === courseFileOrder[nextIndex] && !m.disabled);
          if (entry) return entry;
        }
        return null;
      }
    }
    return getNextInChain(selectedStory?.file, manifestStories);
  }, [selectedStory, manifestStories, courseFileOrder]);

  const handleContinueReading = React.useCallback(async () => {
    if (!lastReadStory?.file) { navigate('/stories'); return; }
    trackContinueReading(lastReadStory);
    try {
      const base = `${process.env.PUBLIC_URL || ''}/Stories`;
      const res = await fetch(`${base}/${lastReadStory.file}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const storyJson = await res.json();
      // Normalize asset paths — prefer the lastReadStory audio if stored, else story JSON
      const manifestEntry = manifestStories.find(m => m.file === lastReadStory.file);
      applyStoryAudio(manifestEntry || { audio: lastReadStory?.audio }, storyJson);
      if (storyJson.image && !/^https?:\/\//.test(storyJson.image))
        storyJson.image = `${process.env.PUBLIC_URL || ''}${storyJson.image}`;
      if (lastReadStory.icon && !/^https?:\/\//.test(lastReadStory.icon))
        storyJson.icon = `${process.env.PUBLIC_URL || ''}${lastReadStory.icon}`;
      else if (lastReadStory.icon)
        storyJson.icon = lastReadStory.icon;
      if (!storyJson.file) storyJson.file = lastReadStory.file;
      if (!storyJson.level) storyJson.level = lastReadStory.level;
      if (!storyJson.series && lastReadStory.series) storyJson.series = lastReadStory.series;
      if (PAYWALL_ENABLED && !hasFullAccess(user)) {
        recordFreeStoryAccess(lastReadStory.file).catch(() => {});
      }
      trackStoryOpened(storyJson);
      setSelectedStory(storyJson);
      navigate('/story/' + fileToSlug(storyJson.file));
    } catch {
      navigate('/stories');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastReadStory, user]);

  // Open a specific story directly from the Welcome page carousel
  const handleOpenStory = React.useCallback(async (entry) => {
    if (!entry?.file) { navigate('/stories'); return; }
    try {
      const base = `${process.env.PUBLIC_URL || ''}/Stories`;
      const res = await fetch(`${base}/${entry.file}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const storyJson = await res.json();
      // Prefer the manifest entry's audio path (same logic as Home.js resolveAudioPath)
      applyStoryAudio(entry, storyJson);
      if (storyJson.image && !/^https?:\/\//.test(storyJson.image))
        storyJson.image = `${process.env.PUBLIC_URL || ''}${storyJson.image}`;
      if (entry.icon && !/^https?:\/\//.test(entry.icon))
        storyJson.icon = `${process.env.PUBLIC_URL || ''}${entry.icon}`;
      else if (entry.icon)
        storyJson.icon = entry.icon;
      storyJson.file = entry.file;
      storyJson.id = entry.file;
      if (!storyJson.level) storyJson.level = entry.level;
      if (!storyJson.series && entry.series) storyJson.series = entry.series;
      // Compute 1-based episode position within the series (matches Home.js openGradedReader logic)
      if (entry._episodeNumber != null) {
        storyJson.episodeNumber = entry._episodeNumber;
      } else if (entry.series) {
        const seriesEps = manifestStories
          .filter(m => m.series === entry.series)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const pos = seriesEps.findIndex(m => m.file === entry.file);
        if (pos >= 0) storyJson.episodeNumber = pos + 1;
      }
      if (user?.uid && storyJson?.file) {
        const compact = { file: storyJson.file, title: storyJson.title, level: storyJson.level, icon: entry.icon, audio: storyJson.audio || null };
        localStorage.setItem(`lastReadStory:${user.uid}`, JSON.stringify(compact));
        setLastReadStory(compact);
      }
      if (PAYWALL_ENABLED && !hasFullAccess(user)) {
        recordFreeStoryAccess(entry.file).catch(() => {});
      }
      trackStoryOpened(storyJson);
      setSelectedStory(storyJson);
      navigate('/story/' + fileToSlug(storyJson.file));
    } catch {
      navigate('/stories');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, manifestStories]);

  // Navigate to home and open a specific series episode selection
  const handleOpenSeries = React.useCallback((seriesKey) => {
    setPendingSeries(seriesKey);
    navigate('/stories');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.body.style.width = '100%';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
  }, []);

  const handleLogout = React.useCallback(async () => {
    trackLogoutEvent();
    await logOut();
    posthog?.reset();
    identifiedUserIdRef.current = null;
    setUser(null);
    navigate('/');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh user premium status after upgrade
  const refreshUserPremiumStatus = React.useCallback(async () => {
    if (!user?.uid) return;
    
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser(prev => ({
          ...prev,
          premium: userData.premium || false
        }));
      }
    } catch (error) {
      console.error('Error refreshing user premium status:', error);
    }
  }, [user?.uid]);

  // ROUTING
  const isWelcomePage = location.pathname === '/';
  return (
    <Box sx={isWelcomePage ? {} : { maxWidth: 1400, mx: 'auto', background: '#FACF9F', minHeight: '100vh' }}>

    {/* Post-upgrade celebration overlay */}
    {showUpgradeSuccess && (
      <Box
        sx={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            background: '#fff',
            borderRadius: 3,
            maxWidth: 420,
            width: '100%',
            p: 4,
            textAlign: 'center',
            boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
          }}
        >
          <Typography sx={{ fontSize: 52, lineHeight: 1, mb: 1.5 }}>🎉</Typography>
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 800, fontSize: 26, color: '#FF6123', mb: 1,
            }}
          >
            You're in!
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic', fontWeight: 700, fontSize: 18, color: '#2c2017', mb: 1.5,
            }}
          >
            Welcome to Dumplingo Premium
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: 15, color: '#6b5a4f', lineHeight: 1.6, mb: 3,
            }}
          >
            All stories are now unlocked. Pick a level below and start reading — your progress saves automatically.
          </Typography>
          <Button
            onClick={() => { setShowUpgradeSuccess(false); navigate('/stories'); }}
            variant="contained"
            fullWidth
            sx={{
              background: '#FF6123', color: '#fff',
              fontFamily: '"Raleway", sans-serif', fontWeight: 700,
              fontSize: 16, py: 1.5, textTransform: 'none', borderRadius: 2,
              '&:hover': { background: '#e55520' },
              mb: 1.5,
            }}
          >
            Start reading now
          </Button>
          <Typography
            onClick={() => setShowUpgradeSuccess(false)}
            sx={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: 13, color: '#b0a09a', cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Dismiss
          </Typography>
        </Box>
      </Box>
    )}
    <ChunkErrorBoundary>
    <Suspense fallback={<Box sx={{ minHeight: '100vh', background: '#FACF9F' }} />}>
    <Routes>
      <Route path="/login" element={
        <>
          <Helmet>
            <title>Login - Dumplingo</title>
            <meta name="robots" content="noindex, nofollow" />
            <link rel="canonical" href="https://dumplingo.net/login" />
            <meta property="og:url" content="https://dumplingo.net/login" />
          </Helmet>
          <Box sx={{
            minHeight: '100vh',
            background: '#FACF9F',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 4,
          }}>
            <AuthForm
              onClose={() => navigate(-1)}
            />
          </Box>
        </>
      } />

      <Route path="/stories" element={
        <>
          <Helmet>
            <title>Stories Library - Dumplingo</title>
            <meta name="description" content="Browse graded Chinese stories and connected reader courses across HSK 1–6. Learn new characters gradually with audio and instant definitions." />
            <link rel="canonical" href="https://dumplingo.net/stories" />
            <meta property="og:url" content="https://dumplingo.net/stories" />
            <meta property="og:type" content="website" />
          </Helmet>
          <Home
            user={user}
            setUser={setUser}
            selectedLevel={selectedLevel}
            initialSeries={pendingSeries}
            onSelectStory={story => {
              setPendingSeries(null);
              trackStoryOpened(story);
              // Only update courseFileOrder if the story explicitly carries one;
              // leaving it undefined (e.g. from EpisodeSelection after a back-nav)
              // means the user is still in the same course — keep existing order.
              if (story._courseFileOrder !== undefined) {
                setCourseFileOrder(story._courseFileOrder);
              }
              if (user?.uid && story?.file) {
                const compact = { file: story.file, title: story.title, level: story.level, icon: story.icon, audio: story.audio || null };
                localStorage.setItem(`lastReadStory:${user.uid}`, JSON.stringify(compact));
                setLastReadStory(compact);
              }
              setSelectedStory(story);
              navigate('/story/' + fileToSlug(story.file));
            }}
            onAbout={() => navigate('/about')}
            onContact={() => {}}
            onLogin={() => navigate('/login')}
            onLogout={handleLogout}
            onGoHome={() => navigate('/')}
            onAccount={() => navigate('/account')}
            onPremiumUpgrade={refreshUserPremiumStatus}
            onBlog={() => navigate('/blog')}
            onVideos={() => navigate('/videos')}
          />
        </>
      } />

      <Route path="/story/*" element={
        <StoryLoader
          manifestStories={manifestStories}
          selectedStory={selectedStory}
          setSelectedStory={setSelectedStory}
          onNavigateAway={() => navigate('/stories')}
        >
          {(story, storySlug) => {
            const levelLabel = story.level ? story.level.replace('HSK', 'HSK ').trim() : 'HSK';
            const description = story.englishSummary
              || `Read "${story.title}" in Mandarin Chinese - ${levelLabel} level story with karaoke audio and tap-for-definition on Dumplingo.`;
            const lrSchema = JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'LearningResource',
              name: story.title,
              description,
              url: `https://dumplingo.net/story/${storySlug}`,
              educationalLevel: levelLabel,
              inLanguage: 'zh',
              learningResourceType: 'Story',
              provider: { '@type': 'Organization', name: 'Dumplingo', url: 'https://dumplingo.net' },
              audience: { '@type': 'EducationalAudience', educationalRole: 'student' },
            });
            return (
              <>
                <Helmet>
                  <title>{`${story.title} - ${levelLabel} Chinese Story | Dumplingo`}</title>
                  <meta name="description" content={description} />
                  <link rel="canonical" href={`https://dumplingo.net/story/${storySlug}`} />
                  <meta property="og:url" content={`https://dumplingo.net/story/${storySlug}`} />
                  <meta property="og:title" content={`${story.title} - ${levelLabel} Chinese Story | Dumplingo`} />
                  <meta property="og:description" content={description} />
                  <meta property="og:type" content="article" />
                  <script type="application/ld+json">{lrSchema}</script>
                </Helmet>
                <Story
                  story={story}
                  nextStory={nextStory}
                  nextStoryLocked={PAYWALL_ENABLED && !hasFullAccess(user) && nextStory ? !isFreeStory(nextStory, manifestStories) : false}
                  onNextStory={nextStory ? () => { trackNextStory(story.file, nextStory); handleOpenStory(nextStory); } : null}
                  inCourse={courseFileOrder.length > 0}
                  onCourseComplete={() => { setCourseFileOrder([]); navigate('/stories'); }}
                  onDone={() => navigate('/stories')}
                  onGoHome={() => navigate('/stories')}
                  onBackToEpisodes={story.series ? () => handleOpenSeries(story.series) : undefined}
                  onAbout={() => navigate('/about')}
                  onContact={() => {}}
                  onLogin={() => navigate('/login')}
                  onLogout={handleLogout}
                  user={user}
                />
              </>
            );
          }}
        </StoryLoader>
      } />

      <Route path="/grammar" element={
        <>
          <Helmet>
            <title>Chinese Grammar Guide | HSK 1–3 | Dumplingo</title>
            <meta name="description" content="Free HSK 1–3 Chinese grammar guides. Clear explanations with examples in simplified Chinese, pinyin, and English." />
            <link rel="canonical" href="https://dumplingo.net/grammar" />
            <meta property="og:url" content="https://dumplingo.net/grammar" />
          </Helmet>
          <Grammar onGoHome={() => navigate('/')} />
        </>
      } />

      <Route path="/grammar/:lessonSlug" element={
        <GrammarLessonRoute />
      } />

      <Route path="/about" element={
        <>
          <Helmet>
            <title>About - Dumplingo</title>
            <meta name="description" content="Dumplingo was built to make learning Chinese feel as natural as reading a novel. Learn about our approach to comprehensible input and the team behind it." />
            <link rel="canonical" href="https://dumplingo.net/about" />
            <meta property="og:url" content="https://dumplingo.net/about" />
            <meta property="og:type" content="website" />
          </Helmet>
          <About />
        </>
      } />

      <Route path="/blog" element={
        <>
          <Helmet>
            <title>Blog - Dumplingo</title>
            <meta name="description" content="Tips, guides, and stories about learning Mandarin Chinese. Comprehensible input, HSK prep, and language learning advice from the Dumplingo team." />
            <link rel="canonical" href="https://dumplingo.net/blog" />
            <meta property="og:url" content="https://dumplingo.net/blog" />
            <meta property="og:type" content="website" />
          </Helmet>
          <Blog
            user={user}
            onAbout={() => navigate('/about')}
            onContact={() => {}}
            onLogin={() => navigate('/login')}
            onLogout={handleLogout}
            onGoHome={() => navigate('/')}
            onAccount={() => navigate('/account')}
            onNavigateToStories={() => navigate('/stories')}
          />
        </>
      } />

      <Route path="/blog/:postId" element={
        <BlogPostRoute
          user={user}
          handleLogout={handleLogout}
          navigate={navigate}
        />
      } />

      <Route path="/videos" element={<Navigate to="/" replace />} />
      <Route path="/video/:videoId" element={<Navigate to="/" replace />} />

      <Route path="/account" element={
        <>
          <Helmet>
            <title>Account - Dumplingo</title>
            <meta name="robots" content="noindex, nofollow" />
            <link rel="canonical" href="https://dumplingo.net/account" />
            <meta property="og:url" content="https://dumplingo.net/account" />
          </Helmet>
          <Account
            user={user}
            onAbout={() => navigate('/about')}
            onContact={() => {}}
            onLogin={() => navigate('/login')}
            onLogout={handleLogout}
            onGoHome={() => navigate('/')}
          />
        </>
      } />

      <Route path="/" element={
        <>
          <Helmet>
            <title>Dumplingo | Learn Chinese Through Stories | Free HSK 1–6 Reading App</title>
            <meta name="description" content="Learn Mandarin Chinese naturally through engaging HSK-levelled stories with audio, karaoke sync, and tap-for-definition. Free to start. No grammar drills." />
            <link rel="canonical" href="https://dumplingo.net/" />
            <meta property="og:url" content="https://dumplingo.net/" />
            <meta property="og:title" content="Dumplingo: Learn Chinese Through Stories" />
            <meta property="og:description" content="Learn Mandarin Chinese naturally through engaging HSK-levelled stories with audio, karaoke sync, and tap-for-definition. Free to start. No grammar drills." />
            <meta property="og:type" content="website" />
          </Helmet>
          <Welcome
            onAbout={() => navigate('/about')}
            onLogin={() => navigate('/login')}
            onLogout={handleLogout}
            onAccount={() => navigate('/account')}
            user={user}
            authLoading={authLoading}
            onNavigateHome={() => { setPendingSeries(null); navigate('/stories'); }}
            onBlog={() => navigate('/blog')}
            onVideos={() => navigate('/videos')}
            lastReadStory={lastReadStory}
            onContinueReading={handleContinueReading}
            onOpenSeries={handleOpenSeries}
            onOpenStory={handleOpenStory}
          />
          {/* Footer */}
          <Box
            component="footer"
            sx={{
              background: '#FACF9F',
              pt: { xs: 5, md: 6 },
              pb: 0,
              px: { xs: 3, sm: 5, md: 8 },
              overflow: 'hidden',
            }}
          >
            {/* Top row: logo + columns */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 4, sm: 0 },
                mb: { xs: 5, md: 6 },
                alignItems: { xs: 'flex-start', sm: 'flex-start' },
              }}
            >
              {/* Logo icon */}
              <Box sx={{ flex: '0 0 auto', mr: { sm: 8, md: 14 } }}>
                <Box
                  component="img"
                  src="/assets/circle_logo.png"
                  alt="Dumplingo"
                  sx={{ width: 72, height: 72, objectFit: 'contain' }}
                />
              </Box>

              {/* Info */}
              <Box sx={{ flex: '0 0 auto', mr: { sm: 10, md: 16 } }}>
                <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1a1a1a', mb: 1.5, letterSpacing: '0.01em' }}>
                  Info
                </Typography>
                {[
                  { label: 'Privacy Policy', href: '/assets/Legal/Termify-Privacy-Policy-QqPSWMCXKF (1).pdf', external: true },
                  { label: 'Terms & Conditions', href: '/assets/Legal/Termify-Terms-and-Conditions-QqPSWMCXKF.pdf', external: true },
                  { label: 'About', href: '/about', external: false },
                ].map(({ label, href, external }) => (
                  <Box key={label} component="a" href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    sx={{ display: 'block', fontFamily: '"Raleway", sans-serif', fontSize: '0.88rem', color: '#1a1a1a', textDecoration: 'none', mb: 0.75, '&:hover': { textDecoration: 'underline' } }}>
                    {label}
                  </Box>
                ))}
              </Box>

              {/* Connect */}
              <Box sx={{ flex: '0 0 auto', mr: { sm: 10, md: 16 } }}>
                <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1a1a1a', mb: 1.5, letterSpacing: '0.01em' }}>
                  Connect
                </Typography>
                {[
                  { label: 'Instagram', href: 'https://www.instagram.com/dumplingo.official/', track: 'instagram' },
                  { label: 'YouTube', href: 'https://www.youtube.com/@Dumplingo.Official', track: 'youtube' },
                  { label: 'TikTok', href: 'https://tiktok.com/@dumplingo7', track: 'tiktok' },
                ].map(({ label, href, track }) => (
                  <Box key={label} component="a" href={href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackSocialClick(track)}
                    sx={{ display: 'block', fontFamily: '"Raleway", sans-serif', fontSize: '0.88rem', color: '#1a1a1a', textDecoration: 'none', mb: 0.75, '&:hover': { textDecoration: 'underline' } }}>
                    {label}
                  </Box>
                ))}
              </Box>

              {/* Feedback */}
              <Box sx={{ flex: '0 0 auto' }}>
                <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1a1a1a', mb: 1.5, letterSpacing: '0.01em' }}>
                  Feedback
                </Typography>
                <Box component="a" href="mailto:dumplingo.official@gmail.com"
                  sx={{ display: 'block', fontFamily: '"Raleway", sans-serif', fontSize: '0.88rem', color: '#1a1a1a', textDecoration: 'none', mb: 0.75, '&:hover': { textDecoration: 'underline' } }}>
                  dumplingo.official@gmail.com
                </Box>
              </Box>
            </Box>

            {/* Big wordmark */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                pt: 0,
                pb: 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: '13vw', sm: '10vw', md: '8vw' },
                  lineHeight: 1,
                  color: '#1a1a1a',
                  letterSpacing: '-0.03em',
                  userSelect: 'none',
                }}
              >
                <Box component="img" src={`${process.env.PUBLIC_URL || ''}/assets/logo.png`} alt="Dumplingo" sx={{ height: { xs: '13vw', sm: '10vw', md: '8vw' }, width: 'auto', display: 'block', filter: 'brightness(0)' }} />
              </Typography>
              <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: '0.72rem', color: '#6b5e52', pb: 0.5, whiteSpace: 'nowrap' }}>
                © {new Date().getFullYear()} Dumplingo
              </Typography>
              </Box>
            </Box>
          </Box>
        </>
      } />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
    </Box>
  );
}

export default App;
